
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';

// Intercept console.warn and console.error to filter out known non-critical Firestore connection timeout logs caused by sandboxed iframe environments
if (typeof window !== 'undefined') {
  const FILTERED_KEYWORDS = [
    'reach Cloud Firestore backend',
    'Backend didn\'t respond within',
    'healthy Internet connection',
    'Firestore (12.',
    'operating in offline mode'
  ];

  const handleConsoleLogging = (originalFn: (...args: any[]) => void) => {
    return (...args: any[]) => {
      const isFiltered = args.some(arg => {
        if (!arg) return false;
        const stringified = typeof arg === 'string' ? arg : JSON.stringify(arg);
        return FILTERED_KEYWORDS.some(kw => stringified.toLowerCase().includes(kw.toLowerCase()));
      });

      if (!isFiltered) {
        originalFn(...args);
      }
    };
  };

  console.warn = handleConsoleLogging(console.warn);
  console.error = handleConsoleLogging(console.error);

  // Suppress uncaught promise rejections originating from Firebase Firestore connection failures in sandbox environment
  window.addEventListener('unhandledrejection', (event) => {
    const errorMsg = event.reason?.message || '';
    if (FILTERED_KEYWORDS.some(kw => errorMsg.toLowerCase().includes(kw.toLowerCase()))) {
      event.preventDefault();
    }
  });
}

class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: any}> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', color: '#e11d48', background: '#0f172a', minHeight: '100vh', fontFamily: 'sans-serif' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '900', textTransform: 'uppercase', marginBottom: '20px' }}>Critical System Failure</h1>
          <div style={{ background: 'rgba(225, 29, 72, 0.1)', border: '1px solid #e11d48', padding: '20px', borderRadius: '12px' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Error Details:</p>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px' }}>{this.state.error?.toString()}</pre>
          </div>
          <button 
            onClick={() => window.location.reload()}
            style={{ marginTop: '20px', padding: '12px 24px', background: '#e11d48', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
          >
            Attempt System Reboot
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );

  // Clear browser caches and unregister service workers to prevent loading stale cached bundles
  if (window.caches) {
    window.caches.keys().then((keys) => {
      keys.forEach((key) => {
        window.caches.delete(key);
      });
    });
  }

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister().then((unregistered) => {
          if (unregistered) {
            console.log('Unregistered service worker to load fresh assets:', registration);
          }
        });
      }
    });
  }
}
